import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Send, MessageCircle } from "lucide-react";
import { formatDate } from "@/lib/format";

// Lote 92 — "Compartilhar" no fim de cada publicação: Equipe + Terceiros vinculados, por mensagem
// interna, e-mail ou WhatsApp. Vai só o resumo (sem partes/cliente); o texto da publicação é opcional
// e fica bloqueado em segredo de justiça.
const RECADOS = ["Dar ciência", "Elaborar manifestação", "Verificar prazo", "Acompanhar"];
const RECADOS_COMPROMISSO = ["Dar ciência", "É de sua responsabilidade", "É um prazo seu", "Por favor, agende", "Verificar prazo"];
const CANAIS = [
  { k: "mensagem", label: "Mensagem no IApauta" },
  { k: "email", label: "E-mail" },
  { k: "whatsapp", label: "WhatsApp" },
];

const ULT_KEY = "iapauta_comp_ultimos";
const lerUltimos = () => { try { return JSON.parse(localStorage.getItem(ULT_KEY) || "null"); } catch { return null; } };
const gravarUltimos = (v) => { try { localStorage.setItem(ULT_KEY, JSON.stringify(v)); } catch { /* sem storage: ok */ } };

const chip = (on, terc) =>
  `px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
    on
      ? terc ? "bg-[#0b4f94] text-white border-[#0b4f94]" : "bg-stone-900 text-white border-stone-900"
      : `bg-white text-stone-700 ${terc ? "border-[#8cc7f5]" : "border-stone-300"} hover:border-stone-500`
  }`;

// Lote 93: o mesmo diálogo serve ao compromisso (audiência/prazo/reunião): `modo="compromisso"` e `item` = a publicação
// aprovada (pub_id + hearing). Lá não há "texto da publicação"; em troca há "Colocar também na agenda dela".
export default function CompartilharPublicacao({ item, open, onClose, modo = "publicacao" }) {
  const comp = modo === "compromisso";
  const [resumo, setResumo] = useState({ equipe: [], terceiros: [] });
  const [selM, setSelM] = useState([]); // member_ids (Equipe)
  const [selU, setSelU] = useState([]); // user_ids (Terceiros)
  const [canal, setCanal] = useState("mensagem");
  const [acao, setAcao] = useState("");
  const [incluirTexto, setIncluirTexto] = useState(false);
  const [copiarAgenda, setCopiarAgenda] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waLinks, setWaLinks] = useState(null);
  const [avulsos, setAvulsos] = useState([]);       // Lote 94: "Outra pessoa" (nome + e-mail/telefone digitados)
  const [avOpen, setAvOpen] = useState(false);
  const [avNome, setAvNome] = useState("");
  const [avContato, setAvContato] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelM([]); setSelU([]); setCanal("mensagem"); setAcao(""); setIncluirTexto(false); setCopiarAgenda(false); setWaLinks(null); setAvulsos([]); setAvOpen(false); setAvNome(""); setAvContato("");
    http.get("/msg/resumo").then((r) => {
      setResumo(r.data);
      // Lote 96: já abre com os últimos destinatários e o último canal marcados (menos toques).
      const ult = lerUltimos();
      if (ult) {
        const mOk = (ult.m || []).filter((id) => (r.data.equipe || []).some((x) => x.member_id === id));
        const uOk = (ult.u || []).filter((id) => (r.data.terceiros || []).some((x) => x.user_id === id));
        if (ult.canal && CANAIS.some((c) => c.k === ult.canal)) setCanal(ult.canal);
        setSelM(mOk); setSelU(uOk);
      }
    }).catch(() => {});
  }, [open, item?.external_id, item?.pub_id]);

  // Terceiros que já são da Equipe (mesmo e-mail) aparecem só uma vez, como Equipe.
  const terceiros = useMemo(() => {
    const idsEq = new Set(resumo.equipe.map((m) => m.vinculado_user_id).filter(Boolean));
    return resumo.terceiros.filter((t) => !idsEq.has(t.user_id));
  }, [resumo]);

  const podeEquipe = (m) => (canal === "mensagem" ? Boolean(m.email || m.vinculado_user_id) : canal === "email" ? Boolean(m.email) : Boolean(m.phone));
  const podeTerceiro = (t) => (canal === "mensagem" || (canal === "email" && Boolean(t.email)));
  const alterna = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  // Trocar de canal tira da seleção quem não tem como receber por ele.
  useEffect(() => {
    setSelM((s) => s.filter((id) => { const m = resumo.equipe.find((x) => x.member_id === id); return m && podeEquipe(m); }));
    setSelU((s) => s.filter((id) => { const t = terceiros.find((x) => x.user_id === id); return t && podeTerceiro(t); }));
    setAvulsos([]); setAvOpen(false);   // o contato digitado vale para um canal só (e-mail ≠ telefone)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canal]);

  if (!item) return null;
  const h = item.hearing || {};
  const secreto = !comp && Boolean(item.is_secret);
  const semTexto = comp || !(item.raw_text || item.summary);
  const total = selM.length + selU.length + avulsos.length;
  const resumoTxt = (comp
    ? [({ audiencia: "Audiência", reuniao: "Compromisso", prazo: "Prazo" })[String(h.deadline_type || h.event_type || "").toLowerCase()] || String(h.deadline_type || h.event_type || "compromisso").replace(/^./, (c) => c.toUpperCase()),
       h.date ? formatDate(h.date) : null, h.time, [h.court, h.court_district].filter(Boolean).join(" — ") || null, h.case_number]
    : ["Publicação", item.published_at ? formatDate(String(item.published_at).slice(0, 10)) : null,
       item.court_origin, item.vara, item.case_number ? `Proc. ${item.case_number}` : null]
  ).filter(Boolean).join(" · ");

  const enviar = async () => {
    if (!total || busy) return;
    setBusy(true);
    try {
      const r = await http.post(
        comp ? `/compromissos/${encodeURIComponent(item.pub_id)}/compartilhar` : `/publicacoes/${encodeURIComponent(item.external_id)}/compartilhar`,
        comp ? { user_ids: selU, member_ids: selM, avulsos, canal, acao, copiar_agenda: copiarAgenda }
             : { user_ids: selU, member_ids: selM, avulsos, canal, acao, incluir_texto: incluirTexto && !secreto },
      );
      const d = r.data;
      if ((d.enviados || []).length || (d.wa || []).length) gravarUltimos({ m: selM, u: selU, canal });
      (d.falhas || []).forEach((f) => toast.error(`${f.nome}: ${f.motivo}`));
      if (canal === "whatsapp" && d.wa?.length) { setWaLinks(d.wa); return; }
      if (d.enviados?.length) { toast.success(`Enviado para ${d.enviados.join(", ")}`); onClose(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível compartilhar.");
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-2xl md:max-w-lg md:mx-auto" data-testid="compartilhar-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">{comp ? "Compartilhar compromisso" : "Compartilhar publicação"}</SheetTitle>
          <SheetDescription className="text-xs">{resumoTxt}</SheetDescription>
        </SheetHeader>

        {waLinks ? (
          <div className="mt-4 space-y-2" data-testid="compartilhar-wa">
            <p className="text-sm text-stone-600">Toque para abrir o WhatsApp de cada pessoa com a mensagem pronta:</p>
            {waLinks.map((w) => (
              <a key={w.nome} href={w.wa_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-800 hover:bg-stone-50">
                <MessageCircle className="w-4 h-4" /> {w.nome}
              </a>
            ))}
            <button type="button" onClick={onClose} className="w-full h-11 rounded-xl bg-stone-900 text-white text-sm font-bold mt-2">Concluir</button>
          </div>
        ) : (
          <div className="mt-3 space-y-4">
            <section>
              <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-stone-500 mb-2">Para quem</div>
              <div className="flex flex-wrap gap-1.5" data-testid="compartilhar-pessoas">
                {resumo.equipe.map((m) => (
                  <button key={m.member_id} type="button" data-testid={`comp-eq-${m.member_id}`} disabled={!podeEquipe(m)}
                    onClick={() => alterna(selM, setSelM, m.member_id)} className={chip(selM.includes(m.member_id), false)}>
                    {m.name} <span className="font-normal opacity-70">(Equipe)</span>
                  </button>
                ))}
                {terceiros.map((t) => (
                  <button key={t.user_id} type="button" data-testid={`comp-ter-${t.user_id}`} disabled={!podeTerceiro(t)}
                    onClick={() => alterna(selU, setSelU, t.user_id)} className={chip(selU.includes(t.user_id), true)}>
                    {t.name || t.email} <span className="font-normal opacity-70">· vinculado</span>
                  </button>
                ))}
                {avulsos.map((a, i) => (
                  <button key={`${a.contato}-${i}`} type="button" data-testid={`comp-avulso-${i}`} onClick={() => setAvulsos((l) => l.filter((_, j) => j !== i))}
                    className={chip(true, false)} title="Remover">
                    {a.nome || a.contato} <span className="font-normal opacity-70">(outra pessoa) ✕</span>
                  </button>
                ))}
                {resumo.equipe.length === 0 && terceiros.length === 0 && avulsos.length === 0 && (
                  <p className="text-xs text-stone-400">Ninguém cadastrado ainda: use "Outra pessoa", cadastre na aba Equipe ou vincule colegas em Chat › Vínculos.</p>
                )}
              </div>
              {canal === "mensagem" ? (
                <p className="text-[11px] text-stone-400 mt-2" data-testid="comp-avulso-dica">Para alguém fora da Equipe, escolha E-mail ou WhatsApp e use "Outra pessoa".</p>
              ) : avOpen ? (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_1.2fr_auto] gap-2 items-end" data-testid="comp-avulso-form">
                  <input data-testid="comp-avulso-nome" value={avNome} onChange={(e) => setAvNome(e.target.value)} placeholder="Nome (opcional)" className="h-10 px-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400" />
                  <input data-testid="comp-avulso-contato" value={avContato} onChange={(e) => setAvContato(e.target.value)} placeholder={canal === "email" ? "nome@exemplo.com" : "(11) 90000-0000"} className="h-10 px-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400" />
                  <button type="button" data-testid="comp-avulso-add" disabled={!avContato.trim()}
                    onClick={() => { setAvulsos((l) => [...l, { nome: avNome.trim() || null, contato: avContato.trim() }]); setAvNome(""); setAvContato(""); setAvOpen(false); }}
                    className="h-10 px-4 rounded-lg bg-stone-900 text-white text-sm font-semibold disabled:opacity-50">Adicionar</button>
                </div>
              ) : (
                <button type="button" data-testid="comp-avulso-abrir" onClick={() => setAvOpen(true)} className="mt-2 text-xs font-semibold text-[#0b4f94] hover:underline">+ Outra pessoa ({canal === "email" ? "e-mail" : "telefone"})</button>
              )}
            </section>

            <section>
              <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-stone-500 mb-2">Como enviar</div>
              <div className="flex flex-wrap gap-1.5">
                {CANAIS.map((c) => (
                  <button key={c.k} type="button" data-testid={`comp-canal-${c.k}`} onClick={() => setCanal(c.k)} className={chip(canal === c.k, false)}>{c.label}</button>
                ))}
              </div>
              {canal === "whatsapp" && <p className="text-[11px] text-stone-400 mt-1.5">Abre o WhatsApp com a mensagem pronta; só para pessoas da Equipe com telefone.</p>}
            </section>

            <section>
              <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-stone-500 mb-2">O que precisa ser feito</div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(comp ? RECADOS_COMPROMISSO : RECADOS).map((r) => (
                  <button key={r} type="button" data-testid={`comp-recado-${r}`} onClick={() => setAcao((a) => (a.trim() ? a : r))} className={chip(acao === r, false)}>{r}</button>
                ))}
              </div>
              <Textarea data-testid="comp-acao" value={acao} maxLength={600} onChange={(e) => setAcao(e.target.value)} placeholder="Ex.: pode preparar a manifestação até dia 30?" className="min-h-[56px] text-sm" />
            </section>

            {comp ? (
              <label className="flex items-start gap-2 text-xs text-stone-600">
                <Switch data-testid="comp-copiar-agenda" checked={copiarAgenda} onCheckedChange={setCopiarAgenda} className="mt-0.5" />
                <span>Colocar também na agenda dela <span className="text-stone-400">(só para quem tem conta no IApauta; a cópia acompanha se você remarcar)</span></span>
              </label>
            ) : (
              <label className="flex items-center gap-2 text-xs text-stone-600" title={secreto ? "Processo em segredo de justiça" : undefined}>
                <Switch data-testid="comp-incluir-texto" checked={incluirTexto && !secreto} disabled={secreto || semTexto} onCheckedChange={setIncluirTexto} />
                Incluir o texto da publicação
                {secreto && <span className="text-stone-400">(bloqueado: segredo de justiça)</span>}
              </label>
            )}
            {comp && canal !== "mensagem" && (
              <p className="text-[11px] text-stone-400 -mt-2" data-testid="comp-aviso-partes">Por e-mail e WhatsApp segue o aviso com as partes e o link para a pessoa confirmar que recebeu (sem login).</p>
            )}

            <div className="rounded-xl bg-stone-100 p-3 text-xs text-stone-600 leading-relaxed" data-testid="comp-previa">
              <div className="font-semibold text-stone-700 mb-0.5">Prévia da mensagem</div>
              {acao.trim() ? <div>{acao.trim()}</div> : <div className="text-stone-400">(sem recado)</div>}
              <div className="mt-1 flex items-start gap-1.5 text-stone-500"><Paperclip className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{resumoTxt} {(!comp || canal === "mensagem") && <span className="text-stone-400">(sem nome de cliente)</span>}</span></div>
              {incluirTexto && !secreto && <div className="mt-1 text-stone-500">+ texto da publicação (até 1.200 caracteres)</div>}
            </div>

            <button type="button" data-testid="comp-enviar" disabled={!total || busy} onClick={enviar}
              className="w-full h-12 rounded-xl bg-stone-900 text-white font-bold text-[15px] flex items-center justify-center gap-2 disabled:opacity-50">
              <Send className="w-4 h-4" />
              {busy ? "Enviando…" : total ? `Enviar para ${total} ${total === 1 ? "pessoa" : "pessoas"}` : "Escolha quem vai receber"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
